import { useBlocker } from "@tanstack/react-router"

import { useAuthStore } from "@/stores/authStore"

interface UseNavigationConfirmationProps {
  shouldBlock: boolean
  allowedPath?: string
  onNavigationConfirm?: () => void
}

interface UseNavigationConfirmationReturn {
  isNavigationBlocked: boolean
  handleNavigationConfirm: () => void
  handleNavigationCancel: () => void
}

// Uses TanStack Router's native history blocker so back/forward and programmatic navigation are
// caught before the URL changes, regardless of whether the current route stays mounted.
export const useNavigationConfirmation = ({
  shouldBlock,
  onNavigationConfirm,
  allowedPath,
}: UseNavigationConfirmationProps): UseNavigationConfirmationReturn => {
  const isAuthenticated = () => useAuthStore.getState().status === "authenticated"

  const { status, proceed, reset } = useBlocker({
    shouldBlockFn: ({ current, next }) => {
      if (!shouldBlock || !isAuthenticated() || next.pathname === current.pathname) {
        return false
      }

      return !allowedPath || !next.pathname.startsWith(allowedPath)
    },
    enableBeforeUnload: () => shouldBlock && isAuthenticated(),
    withResolver: true,
  })

  const handleNavigationConfirm = () => {
    onNavigationConfirm?.()
    proceed?.()
  }

  const handleNavigationCancel = () => {
    reset?.()
  }

  return { isNavigationBlocked: status === "blocked", handleNavigationConfirm, handleNavigationCancel }
}
